import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faHeart as faHeartSolid,
    faChevronLeft,
    faChevronRight,
    faCheck,
    faBookmark as faBookmarkSolid,
} from "@fortawesome/free-solid-svg-icons";
import {
    faHeart as faHeartRegular,
    faComment,
    faPaperPlane,
    faBookmark as faBookmarkRegular,
    faUser,
} from "@fortawesome/free-regular-svg-icons";
import Sidebar from "../components/Sidebar";
import PostViewerModal from "../components/PostViewerModal";
import SharePostModal from "../components/SharePostModal";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import "../styles/feedPage.css";

const PAGE_SIZE = 10;

function timeAgo(dateStr) {
    const now = new Date();
    const diff = Math.floor((now - new Date(dateStr)) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    if (diff < 2592000) return `${Math.floor(diff / 604800)}w`;
    if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo`;
    return `${Math.floor(diff / 31536000)}y`;
}

export default function FeedPage() {
    const { user, profile } = useUser();
    const navigate = useNavigate();

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [allDone, setAllDone] = useState(false);

    // { [postId]: boolean } — bookmark status per post
    const [bookmarks, setBookmarks] = useState({});
    // Share modal
    const [showShare, setShowShare] = useState(false);
    const [sharePost, setSharePost] = useState(null);

    const offsetRef = useRef(0);
    const isFetchingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const followingIdsRef = useRef([]);

    const [selectedPost, setSelectedPost] = useState(null);
    const [selectedPostProfile, setSelectedPostProfile] = useState(null);

    const scrollContainerRef = useRef(null);

    // ── FETCH ──────────────────────────────────────────────────────────────────
    const fetchFeed = useCallback(async (isInitial = false) => {
        if (!user) return;
        if (isFetchingRef.current) return;
        if (!hasMoreRef.current && !isInitial) return;

        isFetchingRef.current = true;
        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        if (isInitial) {
            const { data: followData } = await supabase
                .from("follows")
                .select("following_id")
                .eq("follower_id", user.id);
            followingIdsRef.current = (followData || []).map(f => f.following_id);
        }

        const followingIds = followingIdsRef.current;

        if (followingIds.length === 0) {
            setPosts([]);
            hasMoreRef.current = false;
            setHasMore(false);
            setAllDone(true);
            setLoading(false);
            setLoadingMore(false);
            isFetchingRef.current = false;
            return;
        }

        const from = offsetRef.current;
        const to = from + PAGE_SIZE - 1;

        const { data, error } = await supabase
            .from("posts")
            .select(`
                id, caption, created_at, hide_like_counts, turn_off_commenting, user_id,
                profiles:user_id(id, username, avatar_url),
                post_images(id, image_path, order_index),
                likes(user_id)
            `)
            .in("user_id", followingIds)
            .order("created_at", { ascending: false })
            .range(from, to);

        if (error) {
            setLoading(false);
            setLoadingMore(false);
            isFetchingRef.current = false;
            return;
        }

        const noMorePages = data.length < PAGE_SIZE;
        if (noMorePages) {
            hasMoreRef.current = false;
            setHasMore(false);
        }

        const formatted = await Promise.all(data.map(async (post) => {
            const sorted = (post.post_images || []).sort((a, b) => a.order_index - b.order_index);
            const withUrls = await Promise.all(sorted.map(async (img) => {
                const { data: sd } = await supabase.storage
                    .from("posts").createSignedUrl(img.image_path, 3600);
                return { ...img, signedUrl: sd?.signedUrl || null };
            }));

            const { count: commentCount } = await supabase
                .from("comments")
                .select("id", { count: "exact", head: true })
                .eq("post_id", post.id);

            return {
                ...post,
                post_images: withUrls,
                likesCount: post.likes?.length || 0,
                isLiked: post.likes?.some(l => l.user_id === user.id) || false,
                commentCount: commentCount || 0,
            };
        }));

        offsetRef.current = from + data.length;
        setPosts(prev => isInitial ? formatted : [...prev, ...formatted]);

        // ── Fetch bookmark status for this batch ──
        const postIds = formatted.map(p => p.id);
        if (postIds.length > 0) {
            const { data: bmData } = await supabase
                .from("bookmarks")
                .select("post_id")
                .eq("user_id", user.id)
                .in("post_id", postIds);

            if (bmData) {
                const bm = {};
                bmData.forEach(row => { bm[row.post_id] = true; });
                setBookmarks(prev => ({ ...prev, ...bm }));
            }
        }

        setLoading(false);
        setLoadingMore(false);
        isFetchingRef.current = false;

        if (noMorePages) setAllDone(true);
    }, [user]);

    useEffect(() => {
    document.title = "Home • CodeGram";
  }, []);

    useEffect(() => {
        if (user) {
            offsetRef.current = 0;
            hasMoreRef.current = true;
            setHasMore(true);
            setAllDone(false);
            setBookmarks({});
            fetchFeed(true);
        }
    }, [user]);

    // ── INFINITE SCROLL ────────────────────────────────────────────────────────
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const handleScroll = () => {
            if (isFetchingRef.current || !hasMoreRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < 300) fetchFeed(false);
        };
        container.addEventListener("scroll", handleScroll, { passive: true });
        return () => container.removeEventListener("scroll", handleScroll);
    }, [fetchFeed]);

    // ── LIKE ───────────────────────────────────────────────────────────────────
    const toggleLike = async (postId) => {
        if (!user) return;
        setPosts(prev => prev.map(p => {
            if (p.id !== postId) return p;
            const liked = p.isLiked;
            return { ...p, isLiked: !liked, likesCount: liked ? p.likesCount - 1 : p.likesCount + 1 };
        }));
        const post = posts.find(p => p.id === postId);
        if (!post) return;
        if (post.isLiked) {
            await supabase.from("likes").delete().eq("user_id", user.id).eq("post_id", postId);
        } else {
            await supabase.from("likes").insert({ user_id: user.id, post_id: postId });
        }
    };

    // ── BOOKMARK ───────────────────────────────────────────────────────────────
    const toggleBookmark = async (postId) => {
        if (!user) return;
        const saved = !!bookmarks[postId];

        // Optimistic update
        setBookmarks(prev => ({ ...prev, [postId]: !saved }));

        if (saved) {
            await supabase.from("bookmarks").delete()
                .eq("user_id", user.id).eq("post_id", postId);
        } else {
            await supabase.from("bookmarks").insert({ user_id: user.id, post_id: postId });
        }
    };

    // ── OPEN POST VIEWER ───────────────────────────────────────────────────────
    const openPost = (post) => {
        setSelectedPost(post);
        setSelectedPostProfile(post.profiles);
    };

    const currentIdx = posts.findIndex(p => p.id === selectedPost?.id);

    return (
        <div className="feed-layout">
            <Sidebar />

            <main className="feed-main" ref={scrollContainerRef}>
                <div className="feed-column">

                    {loading && [1, 2, 3].map(i => <PostSkeleton key={i} />)}

                    {!loading && posts.length === 0 && (
                        <div className="feed-empty">
                            <p className="feed-empty-title">Your feed is empty</p>
                            <p className="feed-empty-sub">Follow people to see their posts here.</p>
                        </div>
                    )}

                    {posts.map((post, i) => (
                        <FeedPost
                            key={post.id}
                            post={post}
                            user={user}
                            onLike={() => toggleLike(post.id)}
                            onOpenPost={() => openPost(post)}
                            onBookmark={() => toggleBookmark(post.id)}
                            isBookmarked={!!bookmarks[post.id]}
                            onShare={() => { setSharePost(post); setShowShare(true); }}
                            navigate={navigate}
                            style={{ animationDelay: `${Math.min(i, 5) * 60}ms` }}
                        />
                    ))}

                    {loadingMore && (
                        <div className="feed-loading-more">
                            <div className="feed-spinner" />
                        </div>
                    )}

                    {allDone && !loading && (
                        <div className="feed-all-done">
                            <div className="feed-done-icon">
                                <FontAwesomeIcon icon={faCheck} />
                            </div>
                            <p className="feed-done-title">You're all caught up</p>
                            <p className="feed-done-sub">You've seen all new posts</p>
                        </div>
                    )}
                </div>
            </main>

            {selectedPost && (
                <PostViewerModal
                    open={!!selectedPost}
                    onClose={() => { setSelectedPost(null); setSelectedPostProfile(null); }}
                    post={selectedPost}
                    posts={posts}
                    profile={selectedPostProfile}
                    onNextPost={() => {
                        if (currentIdx < posts.length - 1) {
                            const next = posts[currentIdx + 1];
                            setSelectedPost(next);
                            setSelectedPostProfile(next.profiles);
                        }
                    }}
                    onPrevPost={() => {
                        if (currentIdx > 0) {
                            const prev = posts[currentIdx - 1];
                            setSelectedPost(prev);
                            setSelectedPostProfile(prev.profiles);
                        }
                    }}
                    onPostDeleted={(postId) => {
                        setPosts(prev => prev.filter(p => p.id !== postId));
                        setSelectedPost(null);
                    }}
                    onPostUpdated={(postId, updatedData) => {
                        setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...updatedData } : p));
                        setSelectedPost(prev => prev?.id === postId ? { ...prev, ...updatedData } : prev);
                    }}
                />
            )}

            <SharePostModal
                open={showShare}
                onClose={() => { setShowShare(false); setSharePost(null); }}
                post={sharePost}
            />
        </div>
    );
}

// ── FEED POST CARD ─────────────────────────────────────────────────────────────
function FeedPost({ post, user, onLike, onOpenPost, onBookmark, isBookmarked, onShare, navigate, style }) {
    const [imgIndex, setImgIndex] = useState(0);
    const [captionExpanded, setCaptionExpanded] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const images = post.post_images || [];
    const currentImg = images[imgIndex];

    const caption = post.caption || "";
    const CAPTION_LIMIT = 100;
    const isCaptionLong = caption.length > CAPTION_LIMIT;
    const displayCaption = captionExpanded || !isCaptionLong
        ? caption
        : caption.slice(0, CAPTION_LIMIT);

    return (
        <article className="feed-post" style={style}>

            {/* HEADER */}
            <div className="fp-header">
                <div
                    className="fp-user"
                    onClick={() => navigate(`/user/${encodeURIComponent(post.profiles?.username)}`)}
                >
                    {post.profiles?.avatar_url ? (
                        <img src={post.profiles.avatar_url} className="fp-avatar" alt="" />
                    ) : (
                        <div className="fp-avatar-fallback">
                            <FontAwesomeIcon icon={faUser} />
                        </div>
                    )}
                    <span className="fp-username">{post.profiles?.username}</span>
                </div>
                <div className="fp-meta">
                    <span className="fp-dot">·</span>
                    <span className="fp-time">{timeAgo(post.created_at)}</span>
                </div>
            </div>

            {/* IMAGE */}
            <div className="fp-image-wrap" onDoubleClick={onLike}>
                {!imgLoaded && <div className="fp-img-skeleton" />}
                {currentImg?.signedUrl && (
                    <img
                        src={currentImg.signedUrl}
                        className={`fp-image ${imgLoaded ? "loaded" : "hidden"}`}
                        alt=""
                        onLoad={() => setImgLoaded(true)}
                    />
                )}

                {images.length > 1 && imgIndex > 0 && (
                    <button className="fp-img-arrow left"
                        onClick={e => { e.stopPropagation(); setImgIndex(i => i - 1); setImgLoaded(false); }}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                )}
                {images.length > 1 && imgIndex < images.length - 1 && (
                    <button className="fp-img-arrow right"
                        onClick={e => { e.stopPropagation(); setImgIndex(i => i + 1); setImgLoaded(false); }}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                )}

                {images.length > 1 && (
                    <div className="fp-carousel-dots">
                        {images.map((_, i) => (
                            <span key={i} className={`fp-carousel-dot ${i === imgIndex ? "active" : ""}`} />
                        ))}
                    </div>
                )}
            </div>

            {/* ACTIONS */}
            <div className="fp-actions">
                <div className="fp-actions-left">
                    {/* Like */}
                    <button
                        className={`fp-action-btn ${post.isLiked ? "liked" : ""}`}
                        onClick={onLike}
                        aria-label="Like"
                    >
                        <FontAwesomeIcon icon={post.isLiked ? faHeartSolid : faHeartRegular} />
                    </button>
                    {!post.hide_like_counts && post.likesCount > 0 && (
                        <span className="fp-count">{post.likesCount}</span>
                    )}

                    {/* Comment */}
                    {!post.turn_off_commenting && (
                        <>
                            <button className="fp-action-btn" onClick={onOpenPost} aria-label="Comment">
                                <FontAwesomeIcon icon={faComment} />
                            </button>
                            {post.commentCount > 0 && (
                                <span className="fp-count">{post.commentCount}</span>
                            )}
                        </>
                    )}

                    {/* Share */}
                    <button className="fp-action-btn" aria-label="Share" onClick={onShare}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                </div>

                {/* Bookmark — fully wired */}
                <button
                    className={`fp-action-btn bookmark ${isBookmarked ? "saved" : ""}`}
                    onClick={onBookmark}
                    aria-label={isBookmarked ? "Remove bookmark" : "Save post"}
                >
                    <FontAwesomeIcon icon={isBookmarked ? faBookmarkSolid : faBookmarkRegular} />
                </button>
            </div>

            {/* CAPTION */}
            {caption && (
                <div className="fp-caption">
                    <span
                        className="fp-caption-username"
                        onClick={() => navigate(`/user/${encodeURIComponent(post.profiles?.username)}`)}
                    >
                        {post.profiles?.username}
                    </span>
                    {" "}
                    <span className="fp-caption-text">
                        {displayCaption}
                        {isCaptionLong && !captionExpanded && (
                            <>
                                {"... "}
                                <span className="fp-caption-more" onClick={() => setCaptionExpanded(true)}>
                                    more
                                </span>
                            </>
                        )}
                    </span>
                </div>
            )}
        </article>
    );
}

// ── SKELETON ──────────────────────────────────────────────────────────────────
function PostSkeleton() {
    return (
        <div className="feed-post skeleton-post">
            <div className="fp-header">
                <div className="skel-circle" />
                <div className="skel-line short" />
            </div>
            <div className="skel-image" />
            <div className="fp-actions" style={{ padding: "10px 14px" }}>
                <div className="skel-line short" style={{ width: 80 }} />
            </div>
            <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                <div className="skel-line" style={{ width: "60%" }} />
                <div className="skel-line" style={{ width: "80%" }} />
            </div>
        </div>
    );
}
